/**
 * @ Author: Housefolios
 * @ Create Time: 2021-09-16 13:20:09
 * @ Modified by: David Helmick
 * @ Modified time: 2025-08-14 12:00:13
 * @ Description: Component wrapper to catch and display errors
 */

import React, { useState, useEffect, useRef } from 'react'
import { Box, Button, Typography } from '@mui/material'
import { useApolloClient } from "@apollo/client/react";
import { jwtDecode } from 'jwt-decode'
import { signOut } from '@/housefolios-components/SignOut'
import { GET_ME } from './queries'
import { GET_AFFILIATE } from '@/housefolios-components/CreateAccount/CreateAccountCard/queries'
import { affiliateLogoForHost } from '@/utils/affiliateHosts'
import {
  isChunkLoadError,
  reloadOnceForChunkError,
} from '@/utils/chunkLoadError'

/** @typedef {{ errorId?: string; pathname?: string; lastApolloOperationName?: string; timestamp?: string }} ErrorContext */

function getAffiliateLogoUrl(client, hostname, member) {
  const domain =
    hostname === 'localhost' || hostname === 'dev-admin.housefolios.com'
      ? new URLSearchParams(
          typeof window !== 'undefined' ? window.location.search : '',
        ).get('domain') || hostname
      : hostname

  const urlFromHost = affiliateLogoForHost(domain)
  if (urlFromHost) return urlFromHost

  if (!member) return null
  try {
    const { affiliate } =
      client.readQuery({
        query: GET_AFFILIATE,
        variables: { memberValue: member },
      }) || {}
    const headerImage = affiliate?.createAccount?.headerImage?.[0]?.url
    return headerImage || null
  } catch (_e) {
    return null
  }
}

const ErrorBoundary = ({ children  }) => {
  const client = useApolloClient()
  const [hasError, setHasError] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  /** @type {[ErrorContext, React.Dispatch<React.SetStateAction<ErrorContext>>]} */
  const [errorContext, setErrorContext] = useState({})
  // Prevent window error storms (e.g. React #301) from repeatedly setState'ing.
  const hasErrorRef = useRef(false)

  useEffect(() => {
    const getClientErrorEndpoint = () => {
      const gqlUri = import.meta.env.VITE_URI || '/graphql'
      try {
        const url = new URL(gqlUri, window.location.origin)
        // Common configs use ".../graphql" as the API root; mirror it.
        url.pathname = url.pathname.replace(/\/graphql\/?$/, '/client-error')
        return url.toString()
      } catch (_e) {
        return '/client-error'
      }
    }

    const getDomain = () => {
      const urlParams = new URLSearchParams(window.location.search)
      return urlParams.get('domain') || window.location.hostname || ''
    }

    const getTokenContext = () => {
      const token = localStorage.getItem('token')
      if (!token) return { hasToken: false }
      try {
        /** @type {any} */
        const decoded = jwtDecode(token)
        return {
          hasToken: true,
          userId: decoded?._id || decoded?.userId || decoded?.sub || undefined,
          orgId:
            decoded?.activeOrgId ||
            decoded?.organizationId ||
            decoded?.orgId ||
            undefined,
        }
      } catch (_e) {
        return { hasToken: true }
      }
    }

    const sentKeys = new Set()
    const reportClientError = async (payload) => {
      try {
        const endpoint = getClientErrorEndpoint()
        const isCrossOrigin = (() => {
          try {
            return (
              new URL(endpoint, window.location.origin).origin !==
              window.location.origin
            )
          } catch (_e) {
            return false
          }
        })()

        const key = `${payload.level || ''}|${payload.eventType || ''}|${
          payload.name || ''
        }|${payload.message || ''}|${payload.pathname || ''}`
        if (sentKeys.has(key)) return
        sentKeys.add(key)

        const body = JSON.stringify(payload)

        // Prefer beacon so it still sends on navigation/unload.
        // Note: `sendBeacon` uses credentialed requests (cookies) which
        // requires non-wildcard CORS. When reporting cross-origin, fall back
        // to an explicit `fetch` with credentials omitted to avoid CORS noise.
        if (!isCrossOrigin && navigator?.sendBeacon) {
          const blob = new Blob([body], { type: 'application/json'  })
          navigator.sendBeacon(endpoint, blob)
          return
        }

        // Fallback to keepalive fetch.
        await fetch(endpoint, {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body,
          keepalive: true,
          credentials: 'omit',
        })
      } catch (_e) {
        // never throw from global error handling
      }
    }

    const isBenignAbortOrCancelError = (evt) => {
      // Abort/cancel errors are common during navigation, refetching, tab closes, etc.
      // They should not take over the whole app UI.
      const reason = evt?.reason || evt?.error || evt

      const message =
        typeof evt?.message === 'string'
          ? evt.message
          : typeof reason?.message === 'string'
            ? reason.message
            : ''

      const name =
        typeof reason?.name === 'string'
          ? reason.name
          : typeof evt?.name === 'string'
            ? evt.name
            : ''

      if (name === 'AbortError') return true
      if (name === 'CanceledError') return true // axios v1 cancellation
      if (name === 'CancelError') return true

      return (/signal is aborted without reason/i.test(message) ||
      /aborted/i.test(message) ||
      /request was cancelled/i.test(message) || /cancell?ed/i.test(message));
    }

    const isBenignCrossOriginSecurityError = (evt) => {
      const message =
        typeof evt?.message === 'string'
          ? evt.message
          : typeof evt?.reason?.message === 'string'
            ? evt.reason.message
            : ''

      const name =
        typeof evt?.error?.name === 'string'
          ? evt.error.name
          : typeof evt?.reason?.name === 'string'
            ? evt.reason.name
            : ''

      // Commonly thrown by third-party scripts interacting with cross-origin iframes/windows.
      // Example: "SecurityError: Permission denied to access property 'performance' on cross-origin object"
      if (name === 'SecurityError') return true

      return /permission denied to access property .* on cross-origin object/i.test(
        message,
      );
    }

    // "Failed to fetch" from third-party scripts (Baremetrics/Sentry) or browser extensions
    // intercepting fetch. Common when extensions block Sentry's error-reporting requests.
    const isBenignThirdPartyFetchError = (evt) => {
      const reason = evt?.reason || evt?.error || evt
      const message =
        typeof reason?.message === 'string'
          ? reason.message
          : typeof evt?.message === 'string'
            ? evt.message
            : ''
      const stack =
        typeof reason?.stack === 'string' ? reason.stack : ''

      if (message !== 'Failed to fetch' && !/failed to fetch/i.test(message))
        return false

      return (/baremetrics|sentry|chrome-extension:/i.test(stack) || /baremetrics|sentry/i.test(
        typeof evt?.filename === 'string' ? evt.filename : '',
      ));
    }

    // MetaMask (and similar injected wallets) reject connect() when the user dismisses the
    // prompt, the wallet is locked, etc. Those rejections surface as unhandledrejection from
    // chrome-extension://…/inpage.js — they are not application bugs and should not full-screen.
    const isBenignWalletExtensionError = (evt) => {
      const reason = evt?.reason || evt?.error || evt
      const message =
        typeof reason?.message === 'string' ? reason.message : ''
      const stack =
        typeof reason?.stack === 'string' ? reason.stack : ''

      const metamaskInpage =
        /chrome-extension:\/\/nkbihfbeogaeaoehlefnkodbefgpgknn\//i.test(
          stack,
        )

      if (metamaskInpage) {
        return (/failed to connect/i.test(message) ||
        /user rejected/i.test(message) ||
        /request rejected/i.test(message) ||
        /user denied/i.test(message) || /rejected the request/i.test(message));
      }

      return /^failed to connect to metamask$/i.test(message.trim());
    }

    const handleError = (evt) => {
      if (isBenignCrossOriginSecurityError(evt)) return
      // After a deploy, open tabs request deleted hashed chunks — reload once.
      if (isChunkLoadError(evt) || isChunkLoadError(evt?.error)) {
        if (reloadOnceForChunkError()) return
      }
      const isAbort = isBenignAbortOrCancelError(evt)

      const error = evt?.error
      const pathname = window.location.pathname
      const lastApolloOperationName =
        /** @type {any} */ (window)?.__hfLastApolloOperation?.name || undefined
      const timestamp = new Date().toISOString()
      const errorId = `hf-err-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`

      const basePayload = {
        errorId,
        level: isAbort ? 'info' : 'error',
        eventType: 'error',
        message:
          (typeof evt?.message === 'string' && evt.message) ||
          (typeof error?.message === 'string' && error.message) ||
          'Unknown error',
        name: (typeof error?.name === 'string' && error.name) || undefined,
        stack: (typeof error?.stack === 'string' && error.stack) || undefined,
        url: window.location.href,
        pathname,
        referrer: document.referrer || '',
        domain: getDomain(),
        userAgent: navigator.userAgent,
        ...getTokenContext(),
        lastApolloOperationName,
        lastApolloOperationAt:
          /** @type {any} */ (window)?.__hfLastApolloOperation?.at || undefined,
      }

      reportClientError(basePayload)
      if (isAbort) return
      if (hasErrorRef.current) return
      hasErrorRef.current = true
      // Expand fields explicitly — logging `{ error }` alone often prints "Object".
      console.error(
        '[GlobalError]',
        evt?.message || evt?.error?.message || 'Unknown error',
      )
      console.error('[GlobalError] detail', {
        message: evt?.message,
        filename: evt?.filename,
        lineno: evt?.lineno,
        colno: evt?.colno,
        errorName: evt?.error?.name,
        errorMessage: evt?.error?.message,
        errorStack: evt?.error?.stack,
        pathname: window.location.pathname,
        view: localStorage.getItem('view'),
        portfolio: localStorage.getItem('portfolio'),
        colorMode: localStorage.getItem('hf-color-mode'),
        lastApolloOperationName,
      })
      setHasError(true)
      setErrorMessage(evt?.message || 'Unknown error')
      setErrorContext({
        errorId,
        pathname,
        lastApolloOperationName,
        timestamp,
      })
    }

    const formatRejectionReason = (reason) => {
      if (reason instanceof Error) {
        return {
          message: reason.message || 'Unhandled promise rejection',
          name: reason.name || undefined,
          stack: reason.stack || undefined,
        }
      }

      if (typeof reason === 'string' && reason.trim()) {
        return { message: reason }
      }

      try {
        const serialized = JSON.stringify(reason)
        if (serialized && serialized !== '{}') {
          return { message: serialized }
        }
      } catch (_error) {
        // ignore circular structures
      }

      return { message: 'Unhandled promise rejection' }
    }

    const handleUnhandledRejection = (evt) => {
      if (isBenignCrossOriginSecurityError(evt)) return
      if (isBenignThirdPartyFetchError(evt)) return
      if (isBenignWalletExtensionError(evt)) {
        evt.preventDefault?.()
        return
      }
      // After a deploy, open tabs request deleted hashed chunks — reload once.
      if (isChunkLoadError(evt) || isChunkLoadError(evt?.reason)) {
        if (reloadOnceForChunkError()) return
      }
      const isAbort = isBenignAbortOrCancelError(evt)

      const reason = evt?.reason
      const formattedReason = formatRejectionReason(reason)
      const message =
        formattedReason.message ||
        (typeof evt?.message === 'string' && evt.message) ||
        'Unhandled promise rejection'

      const pathname = window.location.pathname
      const lastApolloOperationName =
        /** @type {any} */ (window)?.__hfLastApolloOperation?.name || undefined
      const timestamp = new Date().toISOString()
      const errorId = `hf-err-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`

      reportClientError({
        errorId,
        level: isAbort ? 'info' : 'error',
        eventType: isAbort ? 'abort' : 'unhandledrejection',
        message,
        name: formattedReason.name,
        stack: formattedReason.stack,
        url: window.location.href,
        pathname,
        referrer: document.referrer || '',
        domain: getDomain(),
        userAgent: navigator.userAgent,
        ...getTokenContext(),
        lastApolloOperationName,
        lastApolloOperationAt:
          /** @type {any} */ (window)?.__hfLastApolloOperation?.at || undefined,
      })
      if (isAbort) return
      if (hasErrorRef.current) return
      hasErrorRef.current = true
      console.error('[UnhandledRejection]', evt?.reason)
      setHasError(true)
      setErrorMessage(message)
      setErrorContext({
        errorId,
        pathname,
        lastApolloOperationName,
        timestamp,
      })
    }

    // Add global error handler
    window.addEventListener('error', handleError)
    window.addEventListener('unhandledrejection', handleUnhandledRejection)

    return () => {
      window.removeEventListener('error', handleError)
      window.removeEventListener(
        'unhandledrejection',
        handleUnhandledRejection,
      )
    }
  }, [])

  if (hasError) {
    const formattedTime = errorContext.timestamp
      ? new Date(errorContext.timestamp).toLocaleString(undefined, {
          dateStyle: 'medium',
          timeStyle: 'short',
        })
      : null

    let affiliateLogoUrl = null
    try {
      const hostname =
        typeof window !== 'undefined' ? window.location.hostname : ''
      const cached = client.readQuery({ query: GET_ME  })
      const member = cached?.me?.activeOrg?.member
      affiliateLogoUrl = getAffiliateLogoUrl(client, hostname, member)
    } catch (_e) {
      // Cache may be empty if error occurred before session loaded
    }

    return (
      <Box sx={{ marginTop: '20%', maxWidth: 560, mx: 'auto', px: 3, textAlign: 'center', color: 'text.primary', }} data-hf="error-boundary">
        {affiliateLogoUrl && (
          <Box
            component="img"
            src={affiliateLogoUrl}
            alt=""
            sx={{
              maxHeight: 80,
              maxWidth: 280,
              objectFit: 'contain',
              mb: 3,
              display: 'block',
              mx: 'auto',
            }}
          />
        )}
        <Typography variant="h4" gutterBottom sx={{
          fontWeight: "bold"
        }}>
          Something went wrong.
        </Typography>
        <Typography
          variant="h6"
          sx={{
            mb: 3,
            color: (theme) =>
              theme.palette.mode === 'dark'
                ? 'rgba(255, 255, 255, 0.78)'
                : 'text.secondary',
          }}
        >
          {errorMessage}
        </Typography>

        <Box
          component="dl"
          sx={{
            textAlign: 'left',
            m: 0,
            p: 2,
            backgroundColor: (theme) => theme.palette.action.hover,
            borderRadius: 1,
            color: 'text.primary',
            '& > div': {
              display: 'flex',
              gap: 1,
              mb: 1,
              '&:last-child': { mb: 0 },
            },
            '& dt': { fontWeight: 600, minWidth: 100, m: 0 },
            '& dd': { m: 0, fontFamily: 'monospace', fontSize: '0.9rem' },
          }}
        >
          {errorContext.errorId && (
            <div>
              <dt>Reference:</dt>
              <dd>{errorContext.errorId}</dd>
            </div>
          )}
          {errorContext.pathname && (
            <div>
              <dt>Page:</dt>
              <dd>{errorContext.pathname}</dd>
            </div>
          )}
          {errorContext.lastApolloOperationName && (
            <div>
              <dt>Last operation:</dt>
              <dd>{errorContext.lastApolloOperationName}</dd>
            </div>
          )}
          {formattedTime && (
            <div>
              <dt>Time:</dt>
              <dd>{formattedTime}</dd>
            </div>
          )}
        </Box>

        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mt: 3 }}>
          <Button
            variant="contained"
            onClick={() => window.location.reload()}
          >
            Refresh
          </Button>
          {typeof window !== 'undefined' &&
            localStorage.getItem('token') && (
              <Button
                variant="outlined"
                onClick={() => signOut(null)}
              >
                Log out
              </Button>
            )}
        </Box>

        <Typography
          variant="body2"
          sx={{
            mt: 3,
            color: (theme) =>
              theme.palette.mode === 'dark'
                ? 'rgba(255, 255, 255, 0.72)'
                : 'text.secondary',
          }}
        >
          Include this reference when contacting support.
        </Typography>
        <Typography variant="body1" sx={{ mt: 1 }}>
          Try refreshing the page. If the error persists, contact support.
        </Typography>
      </Box>
    );
  }
  return children
}

export default ErrorBoundary
